<script setup lang="ts">
import { h } from 'vue';
import type { OpportunityDetailSchema } from '@apparatix/schemas/OpportunityDetail';
import { useDateFns, useStringFunctions, useUtilities } from '@apparatix/composables';
import { Typography, Stack, InputGroup } from '@apparatix/library/core';
import DetailEditorBase from './DetailEditorBase.vue';
import type { Selections } from '@apparatix/composables';

type Props = {
  data: OpportunityDetailSchema;
  selections: Selections;
}

const props = defineProps<Props>();

const { arrayHasValues } = useUtilities();

const { formatDate } = useDateFns();

const { formatAsCurrency } = useStringFunctions();

const getValueOrEmpty = (value: string | number, isCurrency: boolean) => {
  if ((!value && value !== 0) || value === '' || (value === 0 && !isCurrency) || value === 'Invalid date') return 'Empty';
  const result = typeof value === 'number' ? value.toString() : value;
  return !isCurrency ? result : formatAsCurrency(result);
};

const itemVnode = (label: string, value: string | number, isCurrency: boolean = false) => () => h(InputGroup, () => [
  h(Typography, { variant: 'bold-label', class: 'leading-none' }, () => label),
  h(Typography, { variant: 'body', class: 'leading-none', muted: !value || value === 'Invalid date' }, () => getValueOrEmpty(value, isCurrency)),
]);
</script>
<template>
  <DetailEditorBase left-col-header="General" :right-col-header="props.data?.type === 'Opportunity' ? 'Revenue Projection' : ''">
    <template #left-col>
      <Stack :gap="5">
        <component :is="itemVnode('Name', props.data?.name)" />
        <component :is="itemVnode('Owner', props.data?.owner.fullName)" />
        <component :is="itemVnode('Status', props.data?.status?.status)" />
        <component :is="itemVnode('Source', props.data?.source)" />
        <template v-if="props.data.type === 'Opportunity' && arrayHasValues(props.data?.opportunityLostReasons)">
          <component :is="itemVnode('Lost Reasons', props.data?.opportunityLostReasons.join(', '))" />
        </template>
        <component :is="itemVnode('Temperature', props.data?.temperature?.temperature)" />
        <component :is="itemVnode('Value', props.data?.value, true)" v-if="props.data?.type === 'Lead'" />
        <template v-if="props.data.type === 'Opportunity' && props.selections.office.length > 1">
          <component :is="itemVnode('Office', props.data?.office?.office)" />
        </template>
        <template v-if="props.data.type === 'Opportunity'">
          <component :is="itemVnode('Due Date', formatDate(props.data.dueDate, 'MMMM d, uuuu'))" />
          <component :is="itemVnode('Opportunity Budget', props.data.budget, true)" />
        </template>
      </Stack>
    </template>
    <template v-if="props.data?.type === 'Opportunity'" #right-col>
      <Stack :gap="5">
        <component :is="itemVnode('Confidence', props.data.confidenceInterval?.confidencePercentage)" />
        <component :is="itemVnode('Anticipated Start', formatDate(props.data.anticipatedStart, 'MMMM d, uuuu'))" />
        <component :is="itemVnode('Rate', props.data.rate, true)" />
        <component :is="itemVnode('Periods', props.data.periods)" />
        <component :is="itemVnode('Interval', props.data.campaignInterval?.selectionValue)" />
      </Stack>
    </template>
  </DetailEditorBase>
</template>
